import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  LinearProgress,
  TableRow,
  Paper,
  Chip,
  Rating
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts';
import { Download, ArrowBack, Star, TrendingUp, Inventory2, Category } from '@mui/icons-material';
import reportService from '../../../services/reportService';
import excelExport from '../../../utils/excelExport';
import DateRangeFilter from './DateRangeFilter';

const ProductReports = ({ onPageChange }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reportType, setReportType] = useState('product_performance');
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({
    start_date: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    sort_by: 'revenue',
    limit: 20
  });

  const reportTypes = [
    { value: 'product_performance', label: 'Product Performance', icon: <TrendingUp /> },
    { value: 'category_analysis', label: 'Category Analysis', icon: <Category /> },
    { value: 'inventory_health', label: 'Inventory Health', icon: <Inventory2 /> }
  ];

  const sortOptions = [
    { value: 'revenue', label: 'Revenue' },
    { value: 'quantity', label: 'Quantity Sold' },
    { value: 'profit', label: 'Profit' }
  ];

  useEffect(() => {
    loadReportData();
  }, [reportType, filters]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      let response;
      switch (reportType) {
        case 'product_performance':
          response = await reportService.getProductPerformance(filters);
          break;
        case 'category_analysis':
          // Using product performance for category insights
          response = await reportService.getProductPerformance({ ...filters, limit: 50 });
          break;
        case 'inventory_health':
          response = await reportService.getStockVsSales(filters);
          break;
        default:
          response = await reportService.getProductPerformance(filters);
      }
      setReportData(response.data);
    } catch (err) {
      setError('Failed to load product report data: ' + (err.message || 'Unknown error'));
      console.error('Product report loading error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!reportData) return;
    
    try {
      switch (reportType) {
        case 'product_performance':
          excelExport.exportProductPerformance(reportData);
          break;
        case 'inventory_health':
          excelExport.exportInventory(reportData);
          break;
        default:
          excelExport.exportToExcel(reportData, `${reportType}_product_report`);
      }
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export data: ' + err.message);
    }
  };

  const renderProductPerformance = () => {
    if (!reportData?.top_products_pos && !reportData?.top_products_ecommerce) return null;

    const allProducts = [
      ...(reportData.top_products_pos || []),
      ...(reportData.top_products_ecommerce || [])
    ].sort((a, b) => (b.total_revenue || 0) - (a.total_revenue || 0)).slice(0, 15);

    return (
      <Grid container spacing={3}>
        {/* Top Products Chart */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Products by {filters.sort_by === 'revenue' ? 'Revenue' : filters.sort_by === 'quantity' ? 'Quantity' : 'Profit'}
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <BarChart data={allProducts.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Amount']} />
                  <Legend />
                  <Bar 
                    dataKey={filters.sort_by === 'revenue' ? 'total_revenue' : filters.sort_by === 'quantity' ? 'total_quantity' : 'total_profit'} 
                    fill="#8884d8" 
                    name={filters.sort_by === 'revenue' ? 'Revenue' : filters.sort_by === 'quantity' ? 'Quantity' : 'Profit'}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Metrics */}
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Performance Summary</Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary">Total Products Analyzed</Typography>
                <Typography variant="h4">
                  {(reportData.top_products_pos?.length || 0) + (reportData.top_products_ecommerce?.length || 0)}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary">Total Revenue</Typography>
                <Typography variant="h4" color="success.main">
                  ${allProducts.reduce((sum, product) => sum + (product.total_revenue || 0), 0).toLocaleString()}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="textSecondary">Total Profit</Typography>
                <Typography variant="h4" color="info.main">
                  ${allProducts.reduce((sum, product) => sum + (product.total_profit || 0), 0).toLocaleString()}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="textSecondary">Average Profit Margin</Typography>
                <Typography variant="h4" color="warning.main">
                  {allProducts.length > 0 
                    ? (allProducts.reduce((sum, product) => sum + (product.profit_margin || 0), 0) / allProducts.length).toFixed(1)
                    : 0
                  }%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Products Table */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Product Performance Details
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell align="right">Quantity Sold</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">Cost</TableCell>
                      <TableCell align="right">Profit</TableCell>
                      <TableCell align="right">Margin %</TableCell>
                      <TableCell>Channel</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {allProducts.map((product, index) => (
                      <TableRow key={`${product.id}-${index}`} hover>
                        <TableCell>
                          <Typography fontWeight="bold">{product.name}</Typography>
                        </TableCell>
                        <TableCell>{product.sku}</TableCell>
                        <TableCell align="right">{product.total_quantity?.toLocaleString()}</TableCell>
                        <TableCell align="right">${product.total_revenue?.toLocaleString()}</TableCell>
                        <TableCell align="right">${product.total_cost?.toLocaleString()}</TableCell>
                        <TableCell align="right">
                          <Typography 
                            fontWeight="bold"
                            color={product.total_profit > 0 ? 'success.main' : 'error.main'}
                          >
                            ${product.total_profit?.toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${product.profit_margin?.toFixed(1)}%`}
                            color={product.profit_margin > 30 ? 'success' : product.profit_margin > 15 ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={product.channel || 'POS'} 
                            color={product.channel === 'Ecommerce' ? 'primary' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderCategoryAnalysis = () => {
    if (!reportData?.top_products_pos && !reportData?.top_products_ecommerce) return null;

    // Group products by category (simulated data)
    const categoryData = [
      { category: 'Electronics', revenue: 45000, products: 12, profitMargin: 35 },
      { category: 'Clothing', revenue: 32000, products: 8, profitMargin: 28 },
      { category: 'Home & Garden', revenue: 28000, products: 6, profitMargin: 32 },
      { category: 'Sports', revenue: 15000, products: 4, profitMargin: 25 },
      { category: 'Books', revenue: 8000, products: 3, profitMargin: 22 }
    ];

    const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

    return (
      <Grid container spacing={3}>
        {/* Category Revenue */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue by Category
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ category, revenue }) => `${category}: $${(revenue/1000).toFixed(0)}k`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="revenue"
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`$${value.toLocaleString()}`, 'Revenue']} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Category Performance */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Category Performance Metrics
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={categoryData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="category" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="profitMargin" fill="#82ca9d" name="Profit Margin %" />
                  <Bar dataKey="products" fill="#8884d8" name="Products" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Category Details */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Category Analysis</Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Number of Products</TableCell>
                      <TableCell align="right">Total Revenue</TableCell>
                      <TableCell align="right">Average Profit Margin</TableCell>
                      <TableCell align="right">Revenue per Product</TableCell>
                      <TableCell>Performance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {categoryData.map((category, index) => (
                      <TableRow key={category.category} hover>
                        <TableCell>
                          <Typography fontWeight="bold">{category.category}</Typography>
                        </TableCell>
                        <TableCell align="right">{category.products}</TableCell>
                        <TableCell align="right">${category.revenue?.toLocaleString()}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${category.profitMargin}%`}
                            color={category.profitMargin > 30 ? 'success' : category.profitMargin > 20 ? 'warning' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          ${(category.revenue / category.products).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Rating 
                            value={Math.min(5, Math.max(1, Math.floor(category.profitMargin / 6)))} 
                            readOnly 
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderInventoryHealth = () => {
    if (!reportData?.products) return null;

    const inventoryHealth = reportData.products.map(product => ({
      ...product,
      healthScore: product.sales_data?.days_of_stock_remaining > 30 ? 100 :
                  product.sales_data?.days_of_stock_remaining > 15 ? 75 :
                  product.sales_data?.days_of_stock_remaining > 7 ? 50 : 25
    }));

    const healthSummary = {
      excellent: inventoryHealth.filter(p => p.healthScore >= 75).length,
      good: inventoryHealth.filter(p => p.healthScore >= 50 && p.healthScore < 75).length,
      fair: inventoryHealth.filter(p => p.healthScore >= 25 && p.healthScore < 50).length,
      poor: inventoryHealth.filter(p => p.healthScore < 25).length
    };

    return (
      <Grid container spacing={3}>
        {/* Health Overview */}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', bgcolor: '#e8f5e8' }}>
                <CardContent>
                  <Typography color="textSecondary">Excellent</Typography>
                  <Typography variant="h4" color="success.main">{healthSummary.excellent}</Typography>
                  <Typography variant="caption">75-100% Health</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', bgcolor: '#fff3e0' }}>
                <CardContent>
                  <Typography color="textSecondary">Good</Typography>
                  <Typography variant="h4" color="warning.main">{healthSummary.good}</Typography>
                  <Typography variant="caption">50-74% Health</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', bgcolor: '#ffebee' }}>
                <CardContent>
                  <Typography color="textSecondary">Fair</Typography>
                  <Typography variant="h4" color="error.main">{healthSummary.fair}</Typography>
                  <Typography variant="caption">25-49% Health</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ textAlign: 'center', bgcolor: '#f5f5f5' }}>
                <CardContent>
                  <Typography color="textSecondary">Poor</Typography>
                  <Typography variant="h4">{healthSummary.poor}</Typography>
                  <Typography variant="caption">0-24% Health</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        {/* Inventory Health Table */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Inventory Health Analysis ({reportData.analysis_period_days} days)
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell>SKU</TableCell>
                      <TableCell align="right">Current Stock</TableCell>
                      <TableCell align="right">Daily Sales Rate</TableCell>
                      <TableCell align="right">Days of Stock</TableCell>
                      <TableCell>Stock Status</TableCell>
                      <TableCell>Health Score</TableCell>
                      <TableCell>Needs Reorder</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inventoryHealth.map((product) => (
                      <TableRow key={product.id} hover>
                        <TableCell>
                          <Typography fontWeight="bold">{product.name}</Typography>
                        </TableCell>
                        <TableCell>{product.sku}</TableCell>
                        <TableCell align="right">{product.current_stock}</TableCell>
                        <TableCell align="right">{product.sales_data?.daily_sales_rate}</TableCell>
                        <TableCell align="right">{product.sales_data?.days_of_stock_remaining}</TableCell>
                        <TableCell>
                          <Chip 
                            label={product.sales_data?.stock_status} 
                            color={
                              product.sales_data?.stock_status === 'high' ? 'success' :
                              product.sales_data?.stock_status === 'medium' ? 'warning' : 'error'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <LinearProgress 
                            variant="determinate" 
                            value={product.healthScore} 
                            color={
                              product.healthScore >= 75 ? 'success' :
                              product.healthScore >= 50 ? 'warning' : 'error'
                            }
                            sx={{ height: 8, width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={product.needs_reorder ? 'Yes' : 'No'} 
                            color={product.needs_reorder ? 'error' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderReportContent = () => {
    switch (reportType) {
      case 'product_performance':
        return renderProductPerformance();
      case 'category_analysis':
        return renderCategoryAnalysis();
      case 'inventory_health':
        return renderInventoryHealth();
      default:
        return renderProductPerformance();
    }
  };

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => onPageChange('all-reports')}
            variant="outlined"
          >
            Back to Reports
          </Button>
          <Typography variant="h4" fontWeight="bold">
            Product Reports
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={handleExport}
          disabled={!reportData || loading}
        >
          Export Excel
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Report Type</InputLabel>
                <Select
                  value={reportType}
                  label="Report Type"
                  onChange={(e) => setReportType(e.target.value)}
                >
                  {reportTypes.map(type => (
                    <MenuItem key={type.value} value={type.value}>
                      <Box display="flex" alignItems="center">
                        <Box mr={1}>{type.icon}</Box>
                        {type.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={filters.sort_by}
                  label="Sort By"
                  onChange={(e) => setFilters({...filters, sort_by: e.target.value})}
                >
                  {sortOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <DateRangeFilter
                filters={filters}
                onFilterChange={setFilters}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : reportData ? (
        renderReportContent()
      ) : (
        <Card>
          <CardContent>
            <Typography textAlign="center" color="textSecondary">
              No product data available for the selected report type.
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default ProductReports;